import type {
  SupplierIdentifierCheckParams,
  SupplierIdentifierCheckResponse,
} from './types';

import {
  type QueryFunctionContext,
  queryOptions,
  skipToken,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';

import { VestigasHTTPError } from '@vestigas/data';

import { queryKeysSupplier } from '../queryKeys';

import { fetchSupplierIdentifierCheck } from './fetchSupplierIdentifierCheck';

// The register courts qualify a register number rather than identifying a
// company on their own, so they never make a request meaningful by themselves.
const hasSearchCriteria = (params: SupplierIdentifierCheckParams) =>
  Boolean(params.recipientCompanyId) &&
  Boolean(params.vatId || params.hrb || params.hra || params.gln);

/**
 * Generates the query options for the supplier-identifier existence check so
 * they can be shared between the useQuery hook and form-level prefetching. The
 * query is skipped until a recipient company and at least one identifier are
 * provided, since an empty request would not match anything meaningful.
 *
 * @param params - The recipient company and identifiers to check.
 * @param options - Additional options for the query.
 * @returns The query options including queryKey, queryFn, and other settings.
 */
export const getSupplierIdentifierCheckQueryOptions = <
  TData = SupplierIdentifierCheckResponse,
>({
  params,
  options,
}: {
  params: SupplierIdentifierCheckParams | undefined;
  options?: Omit<
    UseQueryOptions<SupplierIdentifierCheckResponse, VestigasHTTPError, TData>,
    'queryKey' | 'queryFn'
  >;
}) =>
  queryOptions({
    queryFn:
      params && hasSearchCriteria(params)
        ? async ({ signal }: QueryFunctionContext) =>
            fetchSupplierIdentifierCheck(params, { signal })
        : skipToken,
    queryKey: queryKeysSupplier.checkIdentifier(params),
    staleTime: 0, // Always perform a fresh identifier check
    ...options,
  });

/**
 * React Query based custom hook for checking whether a supplier identifier
 * (VAT ID, HRA, HRB, and/or GLN) already exists as a connected supplier, a
 * blacklisted company, or a pending request.
 *
 * @param params - The recipient company and identifiers to check.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 * @see https://app.dev.vestigas.com/redoc#tag/Suppliers/operation/check_by_identifier_suppliers_requests_check_get
 */
// fallow-ignore-next-line unused-export -- intended data-layer API surface; the form validator calls the options factory, not this hook.
export const useQuerySupplierIdentifierCheck = (
  params: SupplierIdentifierCheckParams | undefined,
  options?: Omit<
    UseQueryOptions<
      SupplierIdentifierCheckResponse,
      VestigasHTTPError,
      SupplierIdentifierCheckResponse
    >,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery(
    getSupplierIdentifierCheckQueryOptions<SupplierIdentifierCheckResponse>({
      options,
      params,
    }),
  );
